export type DraftDetails = {
    email?: string
    phone?: string
    deliveryAddress?: { line1: string; line2?: string | null; city: string; postal_code: string; country?: string }
}
type Field = keyof DraftDetails
const fields: Field[] = ["email", "phone", "deliveryAddress"]
const fingerprint = (value: unknown) => JSON.stringify(value)

// A quiet queue, separate from Stripe and the final payment submission. Fields
// have independent debounce timers, so typing a phone cannot postpone an email.
export class CheckoutDetailsAutosave {
    private values: DraftDetails = {}
    private saved: Partial<Record<Field, string>> = {}
    private timers = new Map<Field, ReturnType<typeof setTimeout>>()
    private ready = new Set<Field>()
    private active = false
    private busy = false
    private epoch = 0
    private revision: number | undefined
    private controller?: AbortController
    private retryTimer?: ReturnType<typeof setTimeout>
    private failures = 0

    constructor(private url: string, private token: string) {}

    update(values: DraftDetails) {
        for (const field of fields) {
            if (fingerprint(values[field]) === fingerprint(this.values[field])) continue
            clearTimeout(this.timers.get(field))
            this.timers.delete(field)
            this.ready.delete(field)
            if (this.active && values[field] !== undefined && fingerprint(values[field]) !== this.saved[field]) this.schedule(field)
        }
        this.values = values
    }

    setActive(active: boolean) {
        if (active === this.active) return
        if (!active) { this.pause(); return }
        this.active = true
        this.revision = undefined
        this.failures = 0
        for (const field of fields) if (this.needsSave(field)) this.schedule(field)
        void this.drain()
    }

    pause() {
        this.active = false
        this.epoch++
        this.controller?.abort()
        this.busy = false
        clearTimeout(this.retryTimer)
        this.retryTimer = undefined
        for (const timer of this.timers.values()) clearTimeout(timer)
        this.timers.clear()
        this.ready.clear()
    }

    acknowledgeFinal(values: DraftDetails) {
        for (const field of fields) if (values[field] !== undefined) this.saved[field] = fingerprint(values[field])
    }

    flush() {
        if (!this.active) return
        clearTimeout(this.retryTimer)
        this.retryTimer = undefined
        this.failures = 0
        for (const field of fields) {
            clearTimeout(this.timers.get(field))
            this.timers.delete(field)
            if (this.needsSave(field)) this.ready.add(field)
        }
        void this.drain()
    }

    private needsSave(field: Field) {
        return this.values[field] !== undefined && fingerprint(this.values[field]) !== this.saved[field]
    }

    private schedule(field: Field) {
        this.timers.set(field, setTimeout(() => {
            this.timers.delete(field)
            this.ready.add(field)
            this.failures = 0
            void this.drain()
        }, 500))
    }

    private async drain(): Promise<void> {
        if (!this.active || this.busy || this.retryTimer) return
        const patch: Record<string, unknown> = {}
        if (this.revision !== undefined) {
            for (const field of this.ready) if (this.needsSave(field)) patch[field] = this.values[field]
            if (!Object.keys(patch).length) return
        }
        const epoch = this.epoch
        const controller = new AbortController()
        this.controller = controller
        this.busy = true
        const timeout = setTimeout(() => controller.abort(), 4_000)
        let retry = false
        try {
            const response = await fetch(this.url, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ confirmationToken: this.token, revision: this.revision, ...patch }),
                signal: controller.signal, keepalive: true,
            })
            const result = await response.json()
            if (!this.active || epoch !== this.epoch) return
            if (response.status === 409 && result.conflict) {
                // Drop this stale snapshot, not any edits made while it was in
                // flight. Re-read the revision before sending genuinely new edits.
                for (const field of fields) if (field in patch) this.saved[field] = fingerprint(patch[field])
                this.revision = undefined
            } else if (response.ok && Number.isSafeInteger(result.revision)) {
                this.revision = result.revision
                for (const field of fields) if (field in patch) this.saved[field] = fingerprint(patch[field])
                this.failures = 0
            } else if (response.status >= 500 || response.status === 429) {
                retry = true
            } else {
                // Expired/paid sessions and rejected requests must not retry.
                this.pause()
            }
        } catch {
            if (this.active && epoch === this.epoch) retry = true
        } finally {
            clearTimeout(timeout)
            if (epoch === this.epoch) {
                this.busy = false
                this.controller = undefined
                if (retry) {
                    const delay = [1_000, 3_000, 10_000][this.failures++]
                    if (delay !== undefined) this.retryTimer = setTimeout(() => {
                        this.retryTimer = undefined
                        void this.drain()
                    }, delay)
                } else if (this.active) {
                    void this.drain()
                }
            }
        }
    }
}
